<template>
  <v-dialog
    v-model="dialog"
    max-width="680"
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card class="iqd rounded-lg">
      <!-- header: small property details -->
      <div class="iqd__head">
        <div class="iqd__head-text">
          <div class="iqd__eyebrow">{{ typeLabel }}</div>
          <h2 class="iqd__title">{{ propertyLabel || "Propiedad" }}</h2>
          <p class="iqd__sub">
            <template v-if="!loading">
              {{ inquiries.length }} {{ inquiries.length === 1 ? "consulta" : "consultas" }}
              <span v-if="openCount"> · {{ openCount }} abierta{{ openCount === 1 ? "" : "s" }}</span>
            </template>
            <template v-else>Cargando…</template>
          </p>
        </div>
        <button type="button" class="iqd__close" @click="dialog = false" aria-label="Cerrar">
          <hero-icon :icon="XMarkIcon" :size="18" />
        </button>
      </div>

      <div class="iqd__divider"></div>

      <!-- body: clickable inquiry cards (open the full detail) -->
      <div class="iqd__body">
        <div v-if="loading" class="iqd__state">
          <v-progress-circular indeterminate color="accent" size="28" width="2" />
        </div>

        <div v-else-if="!inquiries.length" class="iqd__empty">
          Esta propiedad todavía no tiene consultas.
        </div>

        <template v-else>
          <card
            v-for="i in inquiries"
            :key="i.id"
            :inquiry="i"
            :show-journey="true"
            @reload="fetch"
            @open-property="openProperty"
            @open-journey="$emit('open-journey', $event)"
          ></card>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { Inquiries } from "@/graphql/queries/inquiries";
import Card from "@/components/inquiries/Card";
import HeroIcon from "@/components/shared/HeroIcon";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";

const OPEN = ["pending", "ongoing", "on_hold", "watching"];

export default {
  components: { Card, HeroIcon },

  data: () => ({
    XMarkIcon,
    dialog: false,
    loading: false,
    propertyId: null,
    propertyLabel: "",
    propertyType: null,
    inquiries: []
  }),

  computed: {
    typeLabel() {
      return this.propertyType
        ? this.$t("property.type." + this.propertyType.toLowerCase())
        : "Consultas";
    },
    openCount() {
      return this.inquiries.filter(i => OPEN.includes(i.state)).length;
    }
  },

  methods: {
    open(property) {
      if (!property) return;
      this.propertyId = property.id;
      this.propertyLabel = property.fullAddress || "";
      this.propertyType = property.propertyType || null;
      this.inquiries = [];
      this.dialog = true;
      this.fetch();
    },

    // the inquiry card's "Ver propiedad" — close this dialog and let the parent
    // open the property side-drawer (a fixed drawer can't sit above the dialog)
    openProperty(property) {
      if (!property) return;
      this.$emit("open-property", property);
      this.dialog = false;
    },

    // let the parent close us before it opens a fixed drawer (journey → property)
    close() {
      this.dialog = false;
    },

    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: Inquiries,
          fetchPolicy: "no-cache",
          variables: { propertyId: this.propertyId, perPage: "100", page: "1" }
        })
        .then(res => {
          this.inquiries = res.data.inquiries.inquiries;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.iqd { background: #fff; }

.iqd__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px 14px;
}
.iqd__eyebrow {
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.iqd__title { font-size: 17px; font-weight: 700; color: #1a1814; margin: 3px 0 0; line-height: 1.25; }
.iqd__sub { font-size: 13px; color: #756f64; margin: 4px 0 0; }
.iqd__close {
  flex: none; display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px; background: transparent; border: none;
  cursor: pointer; color: #756f64; transition: background 0.15s, color 0.15s;
}
.iqd__close:hover { background: #f1ece3; color: #1a1814; }
.iqd__divider { height: 1px; background: #ece7de; }

.iqd__body { padding: 16px 22px 22px; max-height: 70vh; overflow-y: auto; }
@media (max-width: 600px) { .iqd__body { max-height: none; } }

/* space the stacked inquiry cards */
.iqd__body >>> .iq-wrap:not(:last-child) { margin-bottom: 10px; }

.iqd__state { display: flex; justify-content: center; padding: 40px 0; }
.iqd__empty {
  border: 1px dashed #dcd4c6; border-radius: 12px; padding: 36px;
  text-align: center; color: #a39a8c; font-size: 14px;
}
</style>
